import { useMemo } from 'react';

import {
  isPaidSubscription,
  isTrialActive,
  type WorkspaceEntitlement,
} from '@trello/commerce/entitlements';
import { Entitlements } from '@trello/entitlements';

export type EntitlementRequired = 'enterprise' | 'free' | 'standardOrPremium';

/**
 * A workspace the current member belongs to, with the fields the cross-flow
 * eligibility checks need.
 *
 * `useTrelloCrossFlow` resolves these once and threads them down, so the
 * underlying provisioning query is subscribed to a single time per tree rather
 * than once per eligibility check (a measured perf bottleneck).
 */
export interface CrossFlowWorkspace {
  id: string;
  displayName: string;
  offering: string;
  idEnterprise: string | null;
  jwmLink: { idCloud: string } | null;
  entitlement: WorkspaceEntitlement | null;
}

const isWorkspaceFree = (
  workspace: Pick<CrossFlowWorkspace, 'offering'>,
): boolean => Entitlements.isFree(workspace.offering);

const isWorkspaceStandardOrPremium = (
  workspace: Pick<CrossFlowWorkspace, 'offering'>,
): boolean =>
  Entitlements.isStandard(workspace.offering) ||
  Entitlements.isPremium(workspace.offering);

const isWorkspaceEnterprise = (
  workspace: Pick<CrossFlowWorkspace, 'offering'>,
): boolean => Entitlements.isEnterprise(workspace.offering);

const isWorkspaceLinkedToJwmSite = (
  workspace: Pick<CrossFlowWorkspace, 'jwmLink'>,
): boolean => !!workspace.jwmLink?.idCloud;

/**
 * Whether a workspace is on an active trial
 */
const isWorkspaceOnTrial = (
  workspace: Pick<CrossFlowWorkspace, 'entitlement'>,
): boolean =>
  workspace.entitlement ? isTrialActive(workspace.entitlement) : false;

const isWorkspaceInEnterprise = (
  workspace: Pick<CrossFlowWorkspace, 'idEnterprise'>,
  enterpriseId?: string,
): boolean => {
  if (!enterpriseId) {
    return true;
  }
  return workspace.idEnterprise === enterpriseId;
};

/**
 * Whether a workspace counts as paid for cross-flow targeting
 */
export const isWorkspacePaid = (workspace: CrossFlowWorkspace): boolean =>
  workspace.entitlement ? isPaidSubscription(workspace.entitlement) : false;

const isWorkspaceEligible = ({
  workspace,
  entitlementRequired,
  enterpriseId,
  treatFreeTrialAsFree,
}: {
  workspace: CrossFlowWorkspace;
  entitlementRequired?: EntitlementRequired;
  enterpriseId?: string;
  treatFreeTrialAsFree?: boolean;
}): boolean => {
  const isTrial = isWorkspaceOnTrial(workspace);
  switch (entitlementRequired) {
    case 'free':
      return (
        (isWorkspaceFree(workspace) ||
          (Boolean(treatFreeTrialAsFree) && isTrial)) &&
        !isWorkspaceLinkedToJwmSite(workspace)
      );
    case 'standardOrPremium':
      return (
        isWorkspaceStandardOrPremium(workspace) &&
        (treatFreeTrialAsFree ? !isTrial : true) &&
        !isWorkspaceLinkedToJwmSite(workspace)
      );
    case 'enterprise':
      return (
        isWorkspaceEnterprise(workspace) &&
        (treatFreeTrialAsFree ? !isTrial : true) &&
        !isWorkspaceLinkedToJwmSite(workspace) &&
        isWorkspaceInEnterprise(workspace, enterpriseId)
      );
    default:
      return (
        !isWorkspaceEnterprise(workspace) &&
        !isWorkspaceLinkedToJwmSite(workspace)
      );
  }
};

export type EligibleWorkspaceOptions = Array<{
  id: string;
  displayName: string;
}>;

/**
 * Derives eligible workspace options for the provisioning and linking flow.
 *
 * Users with multiple eligible workspaces are presented with a workspace
 * selector in the site creation screen, indicating which Trello workspace
 * should be linked to the created JWM site.
 *
 * This hook populates the values for the workspace selector in CFFE, which are
 * represented as `id` and `displayName`.
 */
export const useEligibleWorkspacesForProvisioning = ({
  workspaceId,
  entitlementRequired,
  enterpriseId,
  treatFreeTrialAsFree,
  workspaces,
}: {
  workspaceId?: string;
  entitlementRequired?: EntitlementRequired;
  enterpriseId?: string;
  treatFreeTrialAsFree?: boolean;
  workspaces: CrossFlowWorkspace[];
}): EligibleWorkspaceOptions => {
  return useMemo(() => {
    if (!workspaces.length) {
      return [];
    }
    if (workspaceId) {
      const currentWorkspace = workspaces.find(
        (workspace) => workspace.id === workspaceId,
      );

      // Hide all touchpoints for users that are not members of the current workspace (1)
      // or that are a member of an ineligible workspace (2)

      // 1. If the workspaceId is not found in the list of workspaces,
      // it means the user is not a member of that workspace (ie. a guest).
      if (!currentWorkspace) {
        return [];
      }

      // 2. If the current workspace is ineligible, use this layer as a redundancy
      // to essentially disqualify all workspaces from being eligible, in effect
      // hiding touchpoints. This is a bit undesirable, and we should eventually
      // move this logic up into a different part of the eligibility evaluation.
      if (
        !isWorkspaceEligible({
          workspace: currentWorkspace,
          entitlementRequired,
          enterpriseId,
          treatFreeTrialAsFree,
        })
      ) {
        return [];
      }
    }

    return workspaces
      .filter((workspace) =>
        isWorkspaceEligible({
          workspace,
          entitlementRequired,
          enterpriseId,
          treatFreeTrialAsFree,
        }),
      )
      .map(({ id, displayName }) => ({ id, displayName }));
  }, [
    workspaces,
    workspaceId,
    entitlementRequired,
    enterpriseId,
    treatFreeTrialAsFree,
  ]);
};
